import api from './api';
import type { PromptJob } from './store';

export interface SkillDescriptor {
  id: string;
  title: string;
  description: string;
  category: string;
  effect: 'read-only' | 'generation' | 'local-write' | 'configuration' | 'publishing' | 'external';
  availability: 'recipe' | 'approval-required' | 'unsupported' | 'setup-required';
  prerequisites: string[];
  caveats: string[];
  source: { available: boolean; sha256?: string; relativePath: string; error?: string };
}

export interface FlowkitSkillsCatalog {
  skills: SkillDescriptor[];
  warnings: string[];
}

export interface FlowkitSkillRecipe {
  skill: SkillDescriptor;
  content: string;
  ddlabCaveats: string[];
}

export interface FlowkitSkillProvenanceData {
  execution: 'planning-only';
  loadedSkills: { id: string; sha256: string }[];
  deferredSkills: string[];
  warnings: string[];
}

// Use the existing authenticated client. These methods only read recipes;
// selecting one must never submit it to chat, a CLI, or a generation route.
export const flowkitSkillsApi = {
  async list(signal?: AbortSignal): Promise<FlowkitSkillsCatalog> {
    return (await api.get<FlowkitSkillsCatalog>('/flowkit-gen/skills', { signal })).data;
  },
  async recipe(id: string, signal?: AbortSignal): Promise<FlowkitSkillRecipe> {
    return (await api.get<FlowkitSkillRecipe>(`/flowkit-gen/skills/${encodeURIComponent(id)}`, { signal })).data;
  },
};

// Presence guides planning only, not readiness to render. Counts can survive
// missing preview bytes. Filenames alone are not reference images.
export function hasFlowkitScriptReferences(job: PromptJob, autoImages: readonly string[] = []): boolean {
  return Boolean(job.jobImageUrls?.length || job.fkVaMediaIds?.length || job.batchRpcReferenceMediaIds?.length
    || job.vaMediaIds?.length || (job.fkReferenceCount ?? 0) > 0 || (job.batchRpcReferenceCount ?? 0) > 0
    || (job.ugcMode !== 'faceless' && (job.characterImageUrl || job.fkCharMediaId || job.batchRpcCharacterMediaId || job.charMediaId))
    || autoImages.length);
}